import * as React from 'react';
import { Text } from '@fragments-sdk/ui';
import { joinClassNames } from '@/lib/class-names';
import s from './WorkspacePage.module.scss';

export interface WorkspacePageProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const WorkspacePage = React.forwardRef<
  HTMLDivElement,
  WorkspacePageProps
>(function WorkspacePage({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={joinClassNames(s.root, className)} {...rest}>
      {children}
    </div>
  );
});

export interface WorkspacePageHeaderProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  'title'
> {
  title: React.ReactNode;
  titleAs?: 'h1' | 'h2';
  description?: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}

export function WorkspacePageHeader({
  title,
  titleAs: TitleTag = 'h1',
  description: _description,
  meta,
  actions,
  className,
  ...rest
}: WorkspacePageHeaderProps) {
  return (
    <header className={joinClassNames(s.header, className)} {...rest}>
      <div className={s.headerContent}>
        <Text
          as={TitleTag}
          size="md"
          weight="semibold"
          className={s.title}
        >
          {title}
        </Text>
        {meta ? (
          <Text
            as="div"
            size="xs"
            weight="medium"
            color="tertiary"
            className={s.meta}
          >
            {meta}
          </Text>
        ) : null}
      </div>
      {actions ? <div className={s.headerActions}>{actions}</div> : null}
    </header>
  );
}

export interface WorkspaceToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  start?: React.ReactNode;
  end?: React.ReactNode;
  children?: React.ReactNode;
}

export function WorkspaceToolbar({
  start,
  end,
  children,
  className,
  ...rest
}: WorkspaceToolbarProps) {
  return (
    <div className={joinClassNames(s.toolbar, className)} {...rest}>
      {children ?? (
        <>
          <div className={s.toolbarStart}>{start}</div>
          <div className={s.toolbarEnd}>{end}</div>
        </>
      )}
    </div>
  );
}
